import { observer } from "mobx-react";
import * as Sentry from "@sentry/react";
import React, { useCallback, useContext } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import styles from "./UserRatingModalAssembly.module.css";
import { StarRatingControl } from "src/components/ratingControl/StarRatingControl";

const UserRatingModalAssemblyBase = () => {
    const { t } = useTranslation();
    const { userRatingModalStore } = useContext(RootStoreContext);

    const onChangeQueryComment = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            userRatingModalStore.setComment(event.target.value);
        },
        [userRatingModalStore]
    );

    const onChangeRating = useCallback(
        (rating: number) => {
            userRatingModalStore.setRating(rating);
        },
        [userRatingModalStore]
    );

    const onChangeDoNotAskAgain = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            userRatingModalStore.setDoNotAskAgain(!userRatingModalStore.doNotAskAgain);
        },
        [userRatingModalStore]
    );

    if (userRatingModalStore.isVisibleAssembliesRating) {
        return (
            <UI.Modal
                show={userRatingModalStore.isVisibleAssembliesRating}
                onClose={userRatingModalStore.dismissModal}
                size="lg"
                centered={true}
                isScrollable={true}
            >
                <UI.Col className={"m-2 pr-4"}>
                    <UI.Form.Label className={`${styles.Title}`}>
                        {t("screens.userratingmodalassemblies.title")}
                    </UI.Form.Label>
                    <StarRatingControl
                        rating={userRatingModalStore.rating}
                        nrOfStars={5}
                        onChangeRating={onChangeRating}
                        size={32}
                        disabled={userRatingModalStore.doNotAskAgain}
                    />
                    <UI.Form.Label
                        className={`${styles.InputLabel} ${
                            userRatingModalStore.doNotAskAgain ? styles.InputLabelDisabled : {}
                        }`}
                    >
                        {t("screens.userratingmodalassemblies.feedbackInputLabel")}
                    </UI.Form.Label>
                    <UI.Input
                        type="text"
                        as="textarea"
                        onChange={onChangeQueryComment}
                        disabled={userRatingModalStore.doNotAskAgain}
                        isInvalid={!userRatingModalStore.doNotAskAgain && userRatingModalStore.comment.length === 0}
                    />
                    {userRatingModalStore.isLoading ? (
                        <UI.RotatingSpinner size={30} noLogo={true} className={`${styles.LoadingSpinner}`} />
                    ) : (
                        <div style={{ display: "flex", flexDirection: "row" }}>
                            <UI.Checkbox
                                name={t("screens.userratingmodal.doNotAskAgain")}
                                label={t("screens.userratingmodal.doNotAskAgain")}
                                inline={true}
                                className={`${styles.doNotAskAgain} mr-4`}
                                checked={userRatingModalStore.doNotAskAgain}
                                onChange={onChangeDoNotAskAgain}
                            />
                            <UI.Button
                                className={`${styles.Button}  mr-4`}
                                label={t("screens.userratingmodal.deferButton")}
                                variant="outline-primary"
                                onClick={userRatingModalStore.deferFeedbackAssemblies}
                                disabled={userRatingModalStore.doNotAskAgain}
                                active={userRatingModalStore.doNotAskAgain}
                            />
                            <UI.Button
                                className={`${styles.Button}`}
                                label={
                                    userRatingModalStore.doNotAskAgain
                                        ? t("labels.ok").toUpperCase()
                                        : t("screens.userratingmodal.rateButton")
                                }
                                disabled={
                                    userRatingModalStore.doNotAskAgain === false &&
                                    (userRatingModalStore.rating === 0 || userRatingModalStore.comment.length === 0)
                                        ? true
                                        : false
                                }
                                onClick={
                                    userRatingModalStore.doNotAskAgain
                                        ? userRatingModalStore.deferFeedbackAssemblies
                                        : userRatingModalStore.sendFeedbackAssemblies
                                }
                            />
                        </div>
                    )}
                </UI.Col>
            </UI.Modal>
        );
    } else {
        return <React.Fragment />;
    }
};

export const UserRatingModalAssembly = Sentry.withProfiler(observer(UserRatingModalAssemblyBase));
